import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Zap, CreditCard, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import UpgradeDialog from "./UpgradeDialog";
import { toast } from "sonner";

export default function PlanWidget({ compact = false, onPlanChange }) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const p = await api.billingPlan();
      setPlan(p);
      if (onPlanChange) onPlanChange(p);
    } catch (e) {
      // 401 means user is logged out — silently ignore
    } finally {
      setLoading(false);
    }
  }, [onPlanChange]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const r = await api.billingPortal(window.location.href);
      if (r?.url) window.location.href = r.url;
    } catch (e) {
      toast.error(
        e?.response?.data?.detail ||
          "Could not open Stripe billing portal. Please try again."
      );
    } finally {
      setPortalLoading(false);
    }
  };

  if (loading || !plan) {
    return (
      <div className="cre-surface rounded-sm p-4" data-testid="plan-widget-loading">
        <div className="text-xs text-slate-500">Loading plan…</div>
      </div>
    );
  }

  const isPro = plan.plan === "pro";
  const limit = plan.monthly_analysis_limit || 3;
  const used = plan.used_analysis_count || 0;
  const remaining = plan.remaining_analysis_count ?? Math.max(0, limit - used);
  const pct = Math.min(100, Math.round((used / Math.max(limit, 1)) * 100));

  return (
    <>
      <div
        data-testid="plan-widget"
        className={`cre-surface rounded-sm ${compact ? "p-3" : "p-5"}`}
      >
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="cre-overline text-slate-500">Current plan</span>
              <Badge
                data-testid="plan-name-badge"
                variant="outline"
                className={`rounded-sm font-mono uppercase text-[10px] ${
                  isPro
                    ? "border-violet-500/40 text-violet-700 bg-violet-500/5"
                    : "border-slate-300 text-slate-600"
                }`}
              >
                {isPro ? (
                  <>
                    <Sparkles className="h-3 w-3 mr-1 inline" />
                    Pro
                  </>
                ) : (
                  <>Free</>
                )}
              </Badge>
              {plan.is_admin ? (
                <Badge
                  variant="outline"
                  className="rounded-sm font-mono uppercase text-[10px] border-amber-500/40 text-amber-700 bg-amber-500/5"
                  data-testid="plan-admin-badge"
                >
                  Admin · unrestricted
                </Badge>
              ) : null}
            </div>
            <div className="mt-1 text-xs text-slate-600" data-testid="plan-blurb">
              {isPro
                ? "CRE Pro · 30 reasoning runs / month · for active incident teams"
                : "CRE Free · 3 reasoning runs / month · 1 month history"}
            </div>
            {isPro ? (
              <ul
                data-testid="pro-feature-list"
                className="mt-2 space-y-0.5 text-[11.5px] text-slate-600 leading-snug"
              >
                <li>· Priority incident history</li>
                <li>· Stakeholder-ready RCA summaries</li>
                <li>· CRE Score and reasoning trail</li>
                <li>
                  · Role-wise views — Incident Manager, Support/SRE, Leadership,
                  Management
                </li>
                <li>
                  · Best for teams handling recurring incidents, RCA reviews,
                  service reviews, and stakeholder communication
                </li>
              </ul>
            ) : null}
            <div className="mt-1 text-[11px] text-slate-500">
              Creating or editing incidents does not consume credits. A credit
              is used only when CRE agents are dispatched or analysis is
              re-run.
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isPro && plan.has_stripe_customer ? (
              <Button
                size="sm"
                variant="outline"
                onClick={openPortal}
                data-testid="manage-billing-btn"
                disabled={portalLoading}
                className="rounded-sm"
              >
                {portalLoading ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : (
                  <CreditCard className="h-3.5 w-3.5 mr-1.5" />
                )}
                Manage billing
              </Button>
            ) : null}
            {!isPro ? (
              <Button
                size="sm"
                onClick={() => setUpgradeOpen(true)}
                data-testid="upgrade-pro-btn"
                className="rounded-sm cre-cta-primary text-white"
              >
                <Zap className="h-3.5 w-3.5 mr-1.5" />
                Upgrade to Pro
              </Button>
            ) : null}
          </div>
        </div>

        <div className="mt-4">
          <div className="flex items-baseline justify-between gap-2 text-xs">
            <span className="text-slate-600">Analyses used this period</span>
            <span
              className="font-mono text-slate-700"
              data-testid="plan-usage-counter"
            >
              {used} / {limit}{" "}
              <span className="text-slate-400">
                · {remaining} remaining
              </span>
            </span>
          </div>
          <div className="mt-1.5 h-1.5 bg-slate-200 rounded-sm overflow-hidden">
            <div
              data-testid="plan-usage-bar"
              className={`h-full ${
                pct >= 100
                  ? "bg-rose-500"
                  : pct >= 75
                  ? "bg-amber-500"
                  : isPro
                  ? "bg-violet-500"
                  : "bg-emerald-500"
              }`}
              style={{ width: `${Math.max(pct, 2)}%` }}
            />
          </div>
          <div className="mt-1.5 text-[10px] font-mono text-slate-500">
            {plan.current_period_start && plan.current_period_end ? (
              <>
                Cycle: {plan.current_period_start.slice(0, 10)} →{" "}
                {plan.current_period_end.slice(0, 10)}
              </>
            ) : null}
          </div>
        </div>

        {!isPro && remaining === 0 ? (
          <div
            data-testid="limit-reached-banner"
            className="mt-3 border border-rose-500/30 bg-rose-500/5 rounded-sm p-2.5 text-xs text-rose-700"
          >
            You have used your monthly CRE reasoning runs. Upgrade to CRE Pro
            for 30 reasoning runs / month plus role-wise views and
            stakeholder-ready RCA summaries.
          </div>
        ) : null}
      </div>

      <UpgradeDialog
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
        currentCountry={plan.country}
      />
    </>
  );
}
